import { AnalyticsAdapter } from './analytics-adapter.interface';

export abstract class BaseAnalyticsProvider implements AnalyticsAdapter {
  abstract readonly name: string;
  abstract readonly providerType: string;
  abstract readonly version: string;
  
  protected _isEnabled: boolean = false;
  protected _isInitialized: boolean = false;
  protected _lastError: Error | null = null;
  protected _config: any = {};

  get isEnabled(): boolean {
    return this._isEnabled;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  constructor(config: any = {}) {
    this._config = config;
    this._isEnabled = config?.enabled ?? false;
  }

  // Abstract methods that must be implemented by subclasses
  abstract initialize(): Promise<void>;
  abstract destroy(): Promise<void>;
  abstract trackEvent(eventName: string, properties?: Record<string, any>): Promise<void>;
  abstract identifyUser(userId: string, properties?: Record<string, any>): Promise<void>;
  abstract setUserProperties(properties: Record<string, any>): Promise<void>;
  abstract trackPageView(properties?: Record<string, any>): Promise<void>;

  // Common implementation methods
  protected setEnabled(enabled: boolean): void {
    this._isEnabled = enabled;
  }

  protected setInitialized(initialized: boolean): void {
    this._isInitialized = initialized;
  }

  protected setLastError(error: Error | null): void {
    this._lastError = error;
    if (error) {
      console.error(`[${this.name}] Error:`, error);
    }
  }

  getLastError(): Error | null {
    return this._lastError;
  }

  clearErrors(): void {
    this._lastError = null;
  }

  getProviderInfo(): Record<string, any> {
    return {
      name: this.name,
      type: this.providerType,
      version: this.version,
      enabled: this._isEnabled,
      initialized: this._isInitialized,
      config: this._config
    };
  }

  async updateConfig(config: any): Promise<void> {
    try {
      if (this.validateConfig(config)) {
        this._config = { ...this._config, ...config };
        this._isEnabled = config?.enabled ?? this._isEnabled;
        
        // Reinitialize if already initialized
        if (this._isInitialized) {
          await this.destroy();
          await this.initialize();
        }
      }
    } catch (error) {
      this.setLastError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  validateConfig(config: any): boolean {
    // Base validation - subclasses can override
    return config && typeof config === 'object';
  }

  /**
   * Default session destruction implementation
   * Providers can override this if they support session management
   */
  async destroySession(): Promise<void> {
    this.logInfo('Session destruction not supported by this provider');
  }

  // Helper methods for subclasses
  protected logInfo(message: string, data?: any): void {
    if (this._config?.debugLogs) {
      console.log(`[${this.name}] ${message}`, data || '');
    }
  }

  protected logError(message: string, error?: any): void {
    console.error(`[${this.name}] ${message}`, error || '');
    this.setLastError(error instanceof Error ? error : new Error(message));
  }

  protected logWarning(message: string, data?: any): void {
    if (this._config?.debugLogs) {
      console.warn(`[${this.name}] ${message}`, data || '');
    }
  }
} 